import { unpackToDateTime } from '@campfire/hot-date';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon, RadioButtonUnchecked, Edit } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { Avatar } from '../../../../../general/user-profile/components/Avatar';

import { ActivityTimelinePastReportSession } from '../../__generated__/ActivityTimelinePastReportSession';
import { ActivityTimelinePastSessionReport } from '../../__generated__/ActivityTimelinePastSessionReport';
import { IndividualSessionReportForm } from './form/IndividualSessionReportForm';

type IndividualReportsSummaryProps = {
  sessionReport: ActivityTimelinePastSessionReport;
  session: ActivityTimelinePastReportSession;
  activityDate: string;
  mode: 'optional' | 'mandatory';
  refetch?: () => Promise<any>;
};

export const IndividualReportsSummary = ({
  sessionReport,
  session,
  activityDate,
  mode,
  refetch,
}: IndividualReportsSummaryProps) => {
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const individualReports: any[] = (sessionReport as any).individualSessionReports || [];

  const attendedVolunteers = useMemo(() => {
    return (sessionReport.attendances || [])
      .filter((a: any) => a.__typename === 'VOLUNTEER_VolunteerAttendanceType')
      .map((a: any) => a.volunteer);
  }, [sessionReport.attendances]);

  const submittedVolunteerIds = useMemo(() => new Set(individualReports.map((r: any) => r.volunteer.volunteerId)), [
    individualReports,
  ]);

  const incompleteVolunteers = useMemo(() => {
    if (mode !== 'mandatory') return [];
    return attendedVolunteers.filter((v: any) => !submittedVolunteerIds.has(v.volunteerId));
  }, [mode, attendedVolunteers, submittedVolunteerIds]);

  const editingReport = editingReportId
    ? individualReports.find((r: any) => r.individualSessionReportId === editingReportId)
    : null;

  const effectiveAllowCICO = (sessionReport as any).allowCICO ?? (session as any).activity?.allowCICO ?? false;

  const editingVolunteerId = editingReport?.volunteer?.volunteerId;
  const editingVolunteerProfile = editingReport
    ? {
        preferredName: editingReport.volunteer.profile.preferredName,
        lastName: editingReport.volunteer.profile.lastName,
        avatarUrl: editingReport.volunteer.profile.avatarUrl,
      }
    : undefined;
  const editingExistingAttendance = editingVolunteerId
    ? (sessionReport as any)?.attendances?.some(
        (a: any) =>
          a.__typename === 'VOLUNTEER_VolunteerAttendanceType' && a.volunteer?.volunteerId === editingVolunteerId
      ) ?? false
    : false;
  const editingCICORecord = editingVolunteerId
    ? (sessionReport as any)?.CICOs?.find((c: any) => c.volunteer?.volunteerId === editingVolunteerId)
    : undefined;
  const editingExistingCICO = editingCICORecord
    ? {
        checkIn: editingCICORecord.checkIn ? new Date(editingCICORecord.checkIn) : ('' as Date | ''),
        checkOut: editingCICORecord.checkOut ? new Date(editingCICORecord.checkOut) : ('' as Date | ''),
      }
    : undefined;

  return (
    <Box px={2} py={1}>
      <Divider />
      <Box display='flex' alignItems='center' justifyContent='space-between' mt={2} mb={1}>
        <Typography variant='subtitle2'>{'Individual Reports'}</Typography>
        <Chip
          size='small'
          label={
            mode === 'mandatory'
              ? `${individualReports.length} of ${attendedVolunteers.length} submitted`
              : `${individualReports.length} submitted`
          }
          color={mode === 'mandatory' && individualReports.length < attendedVolunteers.length ? 'secondary' : 'primary'}
          variant='outlined'
        />
      </Box>

      <Grid container spacing={1}>
        {individualReports.map((report: any) => (
          <Grid item xs={12} sm={6} key={report.individualSessionReportId}>
            <IndividualReportCard report={report} onEdit={() => setEditingReportId(report.individualSessionReportId)} />
          </Grid>
        ))}

        {incompleteVolunteers.map((volunteer: any) => (
          <Grid item xs={12} sm={6} key={volunteer.volunteerId}>
            <IncompleteVolunteerCard volunteer={volunteer} />
          </Grid>
        ))}

        {individualReports.length === 0 && incompleteVolunteers.length === 0 && (
          <Grid item xs={12}>
            <Typography variant='body2' color='textSecondary'>
              {'No individual reports have been submitted yet.'}
            </Typography>
          </Grid>
        )}
      </Grid>

      {editingReport && (
        <Dialog open onClose={() => setEditingReportId(null)} fullWidth maxWidth='sm'>
          <DialogTitle>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Typography variant='h6'>{`${editingReport.volunteer.profile.preferredName}'s Report`}</Typography>
              <IconButton onClick={() => setEditingReportId(null)} size='small'>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <IndividualSessionReportForm
              session={session as any}
              activityDate={activityDate}
              existingFieldValues={editingReport.fieldValues}
              onSubmit={() => {
                setEditingReportId(null);
                if (refetch) refetch();
              }}
              onCancel={() => setEditingReportId(null)}
              allowCICO={effectiveAllowCICO}
              existingAttendance={editingExistingAttendance}
              existingCICO={editingExistingCICO}
              volunteerProfile={editingVolunteerProfile}
            />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

const IndividualReportCard = ({ report, onEdit }: { report: any; onEdit: () => void }) => {
  return (
    <Card variant='outlined' style={{ borderColor: '#dadada' }}>
      <CardHeader
        avatar={
          <Avatar
            preferredName={report.volunteer.profile.preferredName}
            lastName={report.volunteer.profile.lastName}
            avatarUrl={report.volunteer.profile.avatarUrl}
            size={28}
          />
        }
        title={
          <Typography variant='body2' style={{ fontWeight: 500, fontSize: '13px' }}>
            {`${report.volunteer.profile.preferredName} ${report.volunteer.profile.lastName}`}
          </Typography>
        }
        subheader={
          <Typography variant='caption' color='textSecondary' style={{ fontSize: '11px' }}>
            {unpackToDateTime(report.submissionDate).toFormat('h:mm a, ccc d LLL')}
          </Typography>
        }
        action={
          <IconButton size='small' onClick={onEdit}>
            <Edit style={{ fontSize: 16 }} />
          </IconButton>
        }
        style={{ padding: '8px 12px 4px' }}
      />
      <CardContent style={{ padding: '0 12px 8px' }}>
        {report.fieldValues && report.fieldValues.length > 0 ? (
          report.fieldValues.slice(0, 3).map((fv: any) => (
            <Box key={fv.fieldValueId || fv.field?.fieldId} mb={0.25}>
              <Typography variant='caption' color='textSecondary' style={{ fontSize: '10px', lineHeight: 1.2 }}>
                {fv.field?.name}
              </Typography>
              <Typography variant='body2' style={{ fontSize: '12px', lineHeight: 1.3 }}>
                {getFieldValueDisplay(fv)}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant='caption' color='textSecondary'>
            {'No responses'}
          </Typography>
        )}
        {report.fieldValues && report.fieldValues.length > 3 && (
          <Typography
            variant='caption'
            color='primary'
            style={{ cursor: 'pointer', fontSize: '11px' }}
            onClick={onEdit}
          >
            {`+${report.fieldValues.length - 3} more fields`}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const IncompleteVolunteerCard = ({ volunteer }: { volunteer: any }) => {
  return (
    <Card variant='outlined' style={{ borderColor: '#dadada', opacity: 0.6 }}>
      <CardHeader
        avatar={
          <Avatar
            preferredName={volunteer.profile.preferredName}
            lastName={volunteer.profile.lastName}
            avatarUrl={volunteer.profile.avatarUrl}
            size={28}
          />
        }
        title={
          <Typography variant='body2' style={{ fontWeight: 500, fontSize: '13px' }}>
            {`${volunteer.profile.preferredName} ${volunteer.profile.lastName}`}
          </Typography>
        }
        subheader={
          <Box display='flex' alignItems='center' gap={0.5}>
            <RadioButtonUnchecked style={{ fontSize: 12, color: '#bbb' }} />
            <Typography variant='caption' color='textSecondary' style={{ fontSize: '11px' }}>
              {'Not submitted'}
            </Typography>
          </Box>
        }
        style={{ padding: '8px 12px' }}
      />
    </Card>
  );
};

function getFieldValueDisplay(fieldValue: any): string {
  switch (fieldValue.__typename) {
    case 'VOLUNTEER_TextFieldValue':
      return fieldValue.textValue || '';
    case 'VOLUNTEER_BooleanFieldValue':
      return fieldValue.boolValue ? 'Yes' : 'No';
    case 'VOLUNTEER_CheckboxFieldValue':
      return fieldValue.checked ? 'Checked' : 'Unchecked';
    case 'VOLUNTEER_NumericFieldValue':
      return String(fieldValue.numericValue ?? '');
    case 'VOLUNTEER_RatingFieldValue':
      return `${fieldValue.ratingValue}/5`;
    case 'VOLUNTEER_TimeFieldValue':
      return fieldValue.timeValue || '';
    case 'VOLUNTEER_DropdownFieldValue':
      return fieldValue.dropdownFieldOptions?.map((o: any) => o.name).join(', ') || '';
    case 'VOLUNTEER_AttachmentFieldValue':
      return `${fieldValue.attachments?.length || 0} attachment(s)`;
    default:
      return '';
  }
}
